import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { openMcpOAuthTab, watchMcpOAuthPopup } from "@/lib/mcp-oauth-popup";
import { messageForMcpCode } from "@/lib/mcp-connect-error";

/** Outcome callbacks for a single MCP OAuth consent flow. Exactly one fires. */
export interface McpOAuthHandlers {
  /** Correlation id from connectMCP's needs_oauth response. */
  state: string;
  /** Consent granted + token exchanged. `systemId` echoes the connected mcp_id. */
  onComplete: (systemId: string) => void;
  /** Provider denied/cancelled, or token exchange failed. */
  onError: (message: string) => void;
  /** Consent tab dismissed/timed out without ever settling. */
  onCancel: () => void;
  /** Optional override for the watcher's wall-clock timeout (ms). */
  timeoutMs?: number;
}

/**
 * Shared controller for the MCP OAuth consent popup, used by EVERY entry point
 * that can launch consent (add wizard modal + page, manage-page reconnect panel +
 * page). Centralises the open → watch → dispose → waiting-state wiring so the four
 * call sites cannot drift — they previously each re-implemented it identically.
 *
 * Returns:
 *  - `waitingForOauth` — true while a consent flow is in flight (drive spinners /
 *    keep the submit button busy with this).
 *  - `setWaitingForOauth` — manual setter for reset-on-open effects.
 *  - `startOAuth(url, handlers)` — opens the consent tab and starts watching it.
 *    Returns false if the browser blocked the popup (caller shows its own
 *    popup-blocked message). Each handler fires at most once; the waiting-state and
 *    the watcher are cleared BEFORE the matching handler runs.
 *  - `disposeOAuth()` — stop watching the current flow without firing a handler
 *    (call from close/cancel handlers).
 *
 * The watcher is auto-disposed on unmount so an in-flight flow cannot leak.
 */
export function useMcpOAuthConnect() {
  const [waitingForOauth, setWaitingForOauth] = useState(false);
  const disposeRef = useRef<(() => void) | null>(null);
  // useTranslations works here because this hook is always called inside a
  // localized route that wraps NextIntlClientProvider (AD-4 / step 27).
  const tMcp = useTranslations("Mcp");

  useEffect(() => () => disposeRef.current?.(), []);

  const disposeOAuth = useCallback(() => {
    disposeRef.current?.();
    disposeRef.current = null;
  }, []);

  const startOAuth = useCallback(
    (authorizationUrl: string, handlers: McpOAuthHandlers): boolean => {
      const popup = openMcpOAuthTab(authorizationUrl);
      if (!popup) return false;

      setWaitingForOauth(true);
      disposeRef.current?.();
      disposeRef.current = watchMcpOAuthPopup(popup, {
        state: handlers.state,
        timeoutMs: handlers.timeoutMs,
        onComplete: (systemId) => {
          disposeRef.current = null;
          setWaitingForOauth(false);
          handlers.onComplete(systemId);
        },
        onError: (error, legacyMessage) => {
          disposeRef.current = null;
          setWaitingForOauth(false);
          // Map the structured error code to localized copy at this single call site
          // (AD-4). legacyMessage is the raw `message` field from older callback pages
          // and is passed as the fallback slot so older pages still surface something
          // meaningful. error_description is never passed here — provider text must
          // not become primary copy (step 28).
          handlers.onError(messageForMcpCode(error, legacyMessage, tMcp));
        },
        onCancel: () => {
          disposeRef.current = null;
          setWaitingForOauth(false);
          handlers.onCancel();
        },
      });
      return true;
    },
    [tMcp],
  );

  return { waitingForOauth, setWaitingForOauth, startOAuth, disposeOAuth };
}
